import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiX, FiChevronRight, FiHome, FiMap } from 'react-icons/fi';
import PropertyCard from '../components/PropertyCard';
import {
  Category,
  Property,
  PropertyCollectionKey,
  PropertyFilterOptions,
} from '../types';
import { categoryService, propertyService } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { Container, Input, Select, Button, Skeleton } from '../components/ui';
import Seo, { organizationSchema } from '../components/Seo';
import { useSettings } from '../contexts/SettingsContext';
import {
  filtersAreEqual,
  formatPropertyCount,
  PropertyFilters,
  readFilters,
  serializeFilters,
} from '../utils/propertySearchFilters';

const COLLECTION_COPY: Record<PropertyCollectionKey, { title: string; subtitle: string }> = {
  destaques: {
    title: 'Imóveis em destaque',
    subtitle: 'Confira os imóveis da nossa seleção com divulgação ampliada.',
  },
  'mais-oportunidades': {
    title: 'Mais oportunidades',
    subtitle: 'Explore os imóveis atualizados mais recentemente.',
  },
};

const EMPTY_FILTER_OPTIONS: PropertyFilterOptions = {
  cities: [],
  neighborhoods: [],
  condominiums: [],
};

const Hero = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 2.5rem 0 2rem;
`;

const Crumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 1rem;

  a:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  svg {
    color: ${({ theme }) => theme.colors.neutral[400]};
  }
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.05rem;
`;

const FilterCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 1.1rem;
  margin-top: 1.75rem;
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.25fr 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 0.85rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.neutral[400]};
  }
  input {
    padding-left: 2.5rem;
  }
`;

const AdvancedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-top: 0.75rem;

  @media (max-width: 980px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PriceFilterHint = styled.p`
  margin-top: 0.65rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.8rem;
  line-height: 1.4;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-top: 1.25rem;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primaryDark};
  border: none;
  padding: 0.35rem 0.7rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  svg {
    opacity: 0.7;
  }
  &:hover svg {
    opacity: 1;
  }
`;

const ClearLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ResultBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 2rem 0 1.25rem;

  span {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const MapLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.card};
`;

const Empty = styled.div`
  text-align: center;
  padding: 4rem 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  .icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    background: ${({ theme }) => theme.colors.surface};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.neutral[400]};
  }
  h3 {
    color: ${({ theme }) => theme.colors.foreground};
    margin-bottom: 0.35rem;
  }
`;

const EndNote = styled.p`
  text-align: center;
  padding: 2rem 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.95rem;
`;

const CenterMore = styled.div`
  display: flex;
  justify-content: center;
  margin: 2.5rem 0 1rem;
`;

const PropertiesPage: React.FC = () => {
  const { siteSettings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState<Property[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filterOptions, setFilterOptions] = useState<PropertyFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [validatedCollection, setValidatedCollection] = useState<PropertyCollectionKey | ''>('');
  const requestGenerationRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);

  const [filters, setFilters] = useState<PropertyFilters>(() => readFilters(searchParams));
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const collection = filters.collection;
    if (!collection) {
      setValidatedCollection('');
      return undefined;
    }

    let cancelled = false;
    setValidatedCollection('');
    propertyService.getShowcase(1).then((showcase) => {
      const contractIsSupported = Boolean(
        showcase?.highlights?.key === 'destaques'
        && Array.isArray(showcase.highlights.items)
        && showcase?.more_opportunities?.key === 'mais-oportunidades'
        && Array.isArray(showcase.more_opportunities.items)
      );
      if (cancelled) return;
      if (contractIsSupported) {
        setValidatedCollection(collection);
        return;
      }
      throw new Error('Backend sem suporte às coleções da vitrine');
    }).catch((error) => {
      if (cancelled || filtersRef.current.collection !== collection) return;
      console.warn('Coleção da vitrine indisponível; exibindo listagem neutra.', error);
      requestGenerationRef.current += 1;
      loadMoreInFlightRef.current = false;
      const normalized = { ...filtersRef.current, collection: '' as const };
      setFilters(normalized);
      setSearchParams(serializeFilters(normalized), { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [filters.collection, setSearchParams]);

  useEffect(() => {
    const loadFilterData = async () => {
      const [categoriesResponse, optionsResponse] = await Promise.allSettled([
        categoryService.getPublic(),
        propertyService.getFilterOptions(),
      ]);
      if (categoriesResponse.status === 'fulfilled') {
        setCategories(categoriesResponse.value);
      } else {
        console.error('Erro ao carregar categorias:', categoriesResponse.reason);
      }
      if (optionsResponse.status === 'fulfilled') {
        setFilterOptions(optionsResponse.value);
      } else {
        console.error('Erro ao carregar opções de localização:', optionsResponse.reason);
      }
    };
    loadFilterData();
  }, []);

  useEffect(() => {
    const urlFilters = readFilters(searchParams);
    if (filtersAreEqual(filtersRef.current, urlFilters)) return;

    requestGenerationRef.current += 1;
    loadMoreInFlightRef.current = false;
    if (!urlFilters.collection) setValidatedCollection('');
    setFilters(urlFilters);
  }, [searchParams]);

  useScrollToTop();

  const fetchProperties = useCallback(
    async (
      isFirstLoad = false,
      pageToLoad = 1,
      generation = requestGenerationRef.current
    ) => {
      try {
        if (isFirstLoad) setLoading(true);
        else {
          loadMoreInFlightRef.current = true;
          setLoadingMore(true);
        }

        const response = await propertyService.search({
          ...(filters.query && { query: filters.query }),
          ...(filters.type && { type: filters.type }),
          ...(filters.transaction_type && { transaction_type: filters.transaction_type }),
          ...(filters.city && { city: filters.city }),
          ...(filters.neighborhood && { neighborhood: filters.neighborhood }),
          ...(filters.condominium && { condominium: filters.condominium }),
          ...(filters.min_price && { min_price: filters.min_price }),
          ...(filters.max_price && { max_price: filters.max_price }),
          ...(filters.bedrooms && { bedrooms: filters.bedrooms }),
          ...(filters.bathrooms && { bathrooms: filters.bathrooms }),
          ...(filters.min_area && { min_area: filters.min_area }),
          ...(filters.max_area && { max_area: filters.max_area }),
          ...(filters.category_id && { category_id: filters.category_id }),
          ...(filters.collection && { collection: filters.collection }),
          status: 'disponivel',
          limit: 9,
          skip: (pageToLoad - 1) * 9,
        });

        if (generation !== requestGenerationRef.current) return;

        if (response && typeof response === 'object' && 'items' in response) {
          const newProperties = response.items || [];
          if (isFirstLoad) {
            setProperties(newProperties);
          } else {
            setProperties((prev) => {
              const existingIds = new Set(prev.map((p: Property) => p.id));
              return [...prev, ...newProperties.filter((p: Property) => !existingIds.has(p.id))];
            });
          }
          setTotal(typeof response.total === 'number' ? response.total : newProperties.length);
          setHasMore(response.has_next || false);
        } else if (Array.isArray(response)) {
          const startIndex = (pageToLoad - 1) * 9;
          const endIndex = startIndex + 9;
          const newProperties = response.slice(startIndex, endIndex);
          if (isFirstLoad) {
            setProperties(newProperties);
          } else {
            setProperties((prev) => {
              const existingIds = new Set(prev.map((p: Property) => p.id));
              return [...prev, ...newProperties.filter((p: Property) => !existingIds.has(p.id))];
            });
          }
          setTotal(response.length);
          setHasMore(endIndex < response.length);
        } else {
          if (isFirstLoad) setProperties([]);
          setTotal(0);
          setHasMore(false);
        }
      } catch (error) {
        if (generation !== requestGenerationRef.current) return;
        console.error('Erro ao carregar propriedades:', error);
        if (isFirstLoad) {
          setProperties([]);
          setTotal(0);
        }
        setHasMore(false);
      } finally {
        if (generation === requestGenerationRef.current) {
          if (isFirstLoad) setLoading(false);
          else {
            loadMoreInFlightRef.current = false;
            setLoadingMore(false);
          }
        }
      }
    },
    [filters]
  );

  const collectionValidationKey = filters.collection ? validatedCollection : '';

  useEffect(() => {
    if (filters.collection && validatedCollection !== filters.collection) {
      setLoading(true);
      return;
    }
    setProperties([]);
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
    loadMoreInFlightRef.current = false;
    fetchProperties(true, 1, requestGenerationRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, collectionValidationKey]);

  const commitFilters = (newFilters: PropertyFilters) => {
    requestGenerationRef.current += 1;
    loadMoreInFlightRef.current = false;
    if (!newFilters.collection) setValidatedCollection('');
    setFilters(newFilters);
    setSearchParams(serializeFilters(newFilters), { replace: true });
  };

  const handleFilterChange = (key: keyof PropertyFilters, value: string) => {
    commitFilters({ ...filters, [key]: value });
  };

  const handleTransactionTypeChange = (value: string) => {
    commitFilters({
      ...filters,
      transaction_type: value,
      min_price: '',
      max_price: '',
    });
  };

  const loadMore = () => {
    if (!loadMoreInFlightRef.current && !loadingMore && hasMore) {
      loadMoreInFlightRef.current = true;
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProperties(false, nextPage, requestGenerationRef.current);
    }
  };

  const removeFilter = (key: keyof PropertyFilters) => {
    if (key === 'transaction_type') {
      handleTransactionTypeChange('');
      return;
    }
    handleFilterChange(key, '');
  };

  const clearAllFilters = () => {
    requestGenerationRef.current += 1;
    loadMoreInFlightRef.current = false;
    setFilters({
      query: '', type: '', transaction_type: '', min_price: '', max_price: '', bedrooms: '',
      bathrooms: '', min_area: '', max_area: '', city: '', neighborhood: '', condominium: '',
      category_id: '', collection: '',
    });
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const getCurrentCategory = () =>
    filters.category_id ? categories.find((c) => c.id.toString() === filters.category_id) : null;

  const currentCategory = getCurrentCategory();
  const currentCollection = filters.collection && validatedCollection === filters.collection
    ? COLLECTION_COPY[filters.collection]
    : null;
  const title = currentCollection?.title || currentCategory?.name || 'Imóveis disponíveis';
  const subtitle = currentCollection?.subtitle || (currentCategory
    ? `Explore nossa seleção na categoria ${currentCategory.name}`
    : 'Encontre o imóvel ideal para comprar ou alugar');
  const priceFiltersEnabled = Boolean(filters.transaction_type);
  const priceFilterMode = filters.transaction_type === 'aluguel'
    ? 'aluguel mensal'
    : 'preço de venda';
  const priceFilterLabel = filters.transaction_type === 'aluguel'
    ? 'Aluguel mensal'
    : filters.transaction_type === 'venda_aluguel'
      ? 'Preço de venda (imóvel misto)'
      : 'Preço de venda';
  const priceFilterHint = !priceFiltersEnabled
    ? 'Escolha Comprar ou Alugar para informar uma faixa de preço.'
    : filters.transaction_type === 'venda_aluguel'
      ? 'Em “Comprar e alugar”, a faixa considera o preço de venda do imóvel.'
      : `A faixa abaixo considera o ${priceFilterMode}.`;

  const activeChips: Array<{ key: keyof PropertyFilters; label: string }> = [];
  if (filters.collection && currentCollection)
    activeChips.push({ key: 'collection', label: `Seleção: ${currentCollection.title}` });
  if (filters.query) activeChips.push({ key: 'query', label: `"${filters.query}"` });
  if (filters.city) activeChips.push({ key: 'city', label: `Cidade: ${filters.city}` });
  if (filters.neighborhood)
    activeChips.push({ key: 'neighborhood', label: `Bairro: ${filters.neighborhood}` });
  if (filters.condominium)
    activeChips.push({ key: 'condominium', label: `Condomínio: ${filters.condominium}` });
  if (filters.type) activeChips.push({ key: 'type', label: `Tipo: ${filters.type}` });
  if (filters.transaction_type) {
    const transactionLabels: Record<string, string> = {
      venda: 'Comprar',
      aluguel: 'Alugar',
      venda_aluguel: 'Comprar e alugar',
    };
    activeChips.push({
      key: 'transaction_type',
      label: `Finalidade: ${transactionLabels[filters.transaction_type] || filters.transaction_type}`,
    });
  }
  if (filters.category_id && currentCategory)
    activeChips.push({ key: 'category_id', label: currentCategory.name });
  if (filters.min_price)
    activeChips.push({ key: 'min_price', label: `${priceFilterMode}: ≥ R$ ${Number(filters.min_price).toLocaleString('pt-BR')}` });
  if (filters.max_price)
    activeChips.push({ key: 'max_price', label: `${priceFilterMode}: ≤ R$ ${Number(filters.max_price).toLocaleString('pt-BR')}` });
  if (filters.bedrooms) activeChips.push({ key: 'bedrooms', label: `${filters.bedrooms}+ quartos` });
  if (filters.bathrooms) activeChips.push({ key: 'bathrooms', label: `${filters.bathrooms}+ banheiros` });
  if (filters.min_area) activeChips.push({ key: 'min_area', label: `≥ ${filters.min_area} m²` });
  if (filters.max_area) activeChips.push({ key: 'max_area', label: `≤ ${filters.max_area} m²` });

  return (
    <>
      <Seo
        title={`Imóveis para comprar e alugar | ${siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária'}`}
        description={`Encontre casas, apartamentos, terrenos e imóveis comerciais para comprar ou alugar. ${total ? `${total} opções disponíveis.` : 'Consulte as opções disponíveis.'}`}
        canonicalPath="/imoveis"
        structuredData={organizationSchema(siteSettings)}
      />
      <Header />

      <Hero>
        <Container>
          <Crumbs>
            <Link to="/">Início</Link>
            <FiChevronRight size={14} />
            <span>Imóveis</span>
          </Crumbs>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>

          <FilterCard>
            <SearchRow>
              <SearchField>
                <FiSearch size={18} />
                <Input
                  type="text"
                  placeholder="Título, código ou palavra-chave"
                  aria-label="Buscar por título, código ou palavra-chave"
                  value={filters.query}
                  onChange={(e) => handleFilterChange('query', e.target.value)}
                />
              </SearchField>
              <Input
                type="text"
                placeholder="Cidade"
                aria-label="Cidade"
                list="properties-filter-cities"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
              <Input
                type="text"
                placeholder="Bairro"
                aria-label="Bairro"
                list="properties-filter-neighborhoods"
                value={filters.neighborhood}
                onChange={(e) => handleFilterChange('neighborhood', e.target.value)}
              />
              <Input
                type="text"
                placeholder="Condomínio ou empreendimento"
                aria-label="Condomínio ou empreendimento"
                list="properties-filter-condominiums"
                value={filters.condominium}
                onChange={(e) => handleFilterChange('condominium', e.target.value)}
              />
            </SearchRow>

            <datalist id="properties-filter-cities">
              {filterOptions.cities.map((option) => (
                <option key={option.value} value={option.value}>
                  {formatPropertyCount(option.count)}
                </option>
              ))}
            </datalist>
            <datalist id="properties-filter-neighborhoods">
              {filterOptions.neighborhoods.map((option) => (
                <option key={option.value} value={option.value}>
                  {formatPropertyCount(option.count)}
                </option>
              ))}
            </datalist>
            <datalist id="properties-filter-condominiums">
              {filterOptions.condominiums.map((option) => (
                <option key={option.value} value={option.value}>
                  {formatPropertyCount(option.count)}
                </option>
              ))}
            </datalist>

            <AdvancedGrid>
              <Select
                value={filters.transaction_type}
                onChange={(e) => handleTransactionTypeChange(e.target.value)}
                aria-label="Finalidade"
              >
                <option value="">Comprar ou alugar</option>
                <option value="venda">Comprar</option>
                <option value="aluguel">Alugar</option>
                <option value="venda_aluguel">Comprar e alugar</option>
              </Select>
              <Select aria-label="Tipo de imóvel" value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
                <option value="">Todos os tipos</option>
                <option value="apartamento">Apartamento</option>
                <option value="casa">Casa</option>
                <option value="terreno">Terreno</option>
                <option value="comercial">Comercial</option>
                <option value="sobrado">Sobrado</option>
                <option value="chacara">Chácara</option>
                <option value="sitio">Sítio</option>
              </Select>
              <Select
                value={filters.category_id}
                onChange={(e) => handleFilterChange('category_id', e.target.value)}
                aria-label="Categoria"
              >
                <option value="">Categorias</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                placeholder={priceFiltersEnabled ? `${priceFilterLabel} mín.` : 'Escolha a finalidade'}
                aria-label={priceFiltersEnabled ? `${priceFilterLabel} mínimo` : 'Escolha a finalidade antes do preço mínimo'}
                title={priceFilterHint}
                disabled={!priceFiltersEnabled}
                value={filters.min_price}
                onChange={(e) => handleFilterChange('min_price', e.target.value)}
              />
              <Input
                type="number"
                placeholder={priceFiltersEnabled ? `${priceFilterLabel} máx.` : 'Escolha a finalidade'}
                aria-label={priceFiltersEnabled ? `${priceFilterLabel} máximo` : 'Escolha a finalidade antes do preço máximo'}
                title={priceFilterHint}
                disabled={!priceFiltersEnabled}
                value={filters.max_price}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
              />
              <Select aria-label="Quantidade mínima de quartos" value={filters.bedrooms} onChange={(e) => handleFilterChange('bedrooms', e.target.value)}>
                <option value="">Quartos</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
              </Select>
              <Input
                type="number"
                placeholder="Área mín. m²"
                aria-label="Área mínima em metros quadrados"
                value={filters.min_area}
                onChange={(e) => handleFilterChange('min_area', e.target.value)}
              />
              <Input
                type="number"
                placeholder="Área máx. m²"
                aria-label="Área máxima em metros quadrados"
                value={filters.max_area}
                onChange={(e) => handleFilterChange('max_area', e.target.value)}
              />
            </AdvancedGrid>
            <PriceFilterHint aria-live="polite">{priceFilterHint}</PriceFilterHint>
          </FilterCard>

          {activeChips.length > 0 && (
            <Chips>
              {activeChips.map((chip) => (
                <Chip
                  key={chip.key}
                  onClick={() => removeFilter(chip.key)}
                  aria-label={`Remover filtro ${chip.label}`}
                >
                  {chip.label} <FiX size={14} />
                </Chip>
              ))}
              <ClearLink onClick={clearAllFilters} aria-label="Limpar todos os filtros">Limpar filtros</ClearLink>
            </Chips>
          )}
        </Container>
      </Hero>

      <Container>
        <ResultBar role="status" aria-live="polite" aria-atomic="true">
          <span>
            {loading
              ? 'Buscando imóveis...'
              : `${total} ${total === 1 ? 'imóvel encontrado' : 'imóveis encontrados'}`}
          </span>
          <MapLink to={{ pathname: '/mapa', search: searchParams.toString() }}>
            <FiMap size={16} /> Buscar no mapa
          </MapLink>
        </ResultBar>

        {loading ? (
          <Grid>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i}>
                <Skeleton $h="0" $w="100%" style={{ aspectRatio: '4 / 3' }} $radius="0" />
                <div style={{ padding: '1.1rem' }}>
                  <Skeleton $h="1.1rem" $w="80%" />
                  <Skeleton $h="0.85rem" $w="55%" style={{ marginTop: '0.7rem' }} />
                  <Skeleton $h="0.85rem" $w="70%" style={{ marginTop: '1.2rem' }} />
                </div>
              </SkeletonCard>
            ))}
          </Grid>
        ) : properties.length === 0 ? (
          <Empty>
            <div className="icon">
              <FiHome size={28} />
            </div>
            <h3>Nenhum imóvel encontrado</h3>
            <p>Tente ajustar os filtros ou limpar a busca.</p>
            {activeChips.length > 0 && (
              <div style={{ marginTop: '1.25rem' }}>
                <Button $variant="outline" onClick={clearAllFilters} aria-label="Limpar todos os filtros">
                  Limpar filtros
                </Button>
              </div>
            )}
          </Empty>
        ) : (
          <>
            <Grid>
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </Grid>

            {hasMore ? (
              <CenterMore>
                <Button $variant="outline" $size="lg" onClick={loadMore} disabled={loadingMore} aria-label="Carregar mais imóveis">
                  {loadingMore ? 'Carregando...' : 'Carregar mais imóveis'}
                </Button>
              </CenterMore>
            ) : (
              <EndNote>
                Você viu todos os {total}{' '}
                {total === 1 ? 'imóvel disponível' : 'imóveis disponíveis'}.
              </EndNote>
            )}
          </>
        )}
      </Container>

      <Footer />
    </>
  );
};

export default PropertiesPage;
